import React, { useState } from "react";
import { HiArrowPath, HiXMark } from "react-icons/hi2";
import MainButton, {
  CancelButton,
  SaveButton,
} from "../../components/common/MainButtonComponent";
import "./AssessShared.css";
import "./ManagePaper.css";

const getStatusClass = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "published") return "paper-status paper-status--published";
  if (normalized === "ready") return "paper-status paper-status--ready";
  if (normalized === "reviewcompleted")
    return "paper-status paper-status--completed";
  return "paper-status";
};

const OverrideRadioGroup = ({ label, defaultValue = "off" }) => {
  const [value, setValue] = useState(defaultValue);

  return (
    <div className="override-field">
      <span>{label}</span>
      <div
        className="override-radio-group"
        role="radiogroup"
        aria-label={label}
      >
        {["on", "off"].map((option) => (
          <label
            className={`override-radio override-radio--${option} ${
              value === option ? "active" : ""
            }`}
            key={option}
          >
            <input
              type="radio"
              name={label}
              value={option}
              checked={value === option}
              onChange={() => setValue(option)}
            />
            <span className="override-radio__dot" />
            {option === "on" ? "On" : "Off"}
          </label>
        ))}
      </div>
    </div>
  );
};

const AddPaperConfirm = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <div className="manage-paper-modal" role="dialog" aria-modal="true">
      <button
        type="button"
        className="manage-paper-modal__backdrop"
        onClick={onClose}
        aria-label="Close add paper confirmation"
      />
      <div className="manage-paper-modal__card">
        <div className="manage-paper-modal__icon">?</div>
        <h2>Are you sure?</h2>
        <p>Do you want to add this for available exams?</p>
        <div className="manage-paper-modal__actions">
          <SaveButton label="Yes, add it!" onClick={onClose} />
          <CancelButton label="Cancel" onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

const OverrideDrawer = ({ open, onClose }) => (
  <>
    {open && (
      <button
        type="button"
        className="paper-comments-backdrop"
        onClick={onClose}
        aria-label="Close override details"
      />
    )}
    <aside
      className={`manage-paper-drawer ${open ? "open" : ""}`}
      aria-hidden={!open}
    >
      <div className="manage-paper-drawer__header">
        <h2>Override Details</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close override details"
        >
          <HiXMark aria-hidden="true" />
        </button>
      </div>
      <div className="manage-paper-drawer__body">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <OverrideRadioGroup label="Credit Purchase" defaultValue="off" />
          </div>
          <div className="col-12 col-md-6">
            <OverrideRadioGroup label="Cash Purchase" defaultValue="on" />
          </div>
          <div className="col-12 col-md-6">
            <OverrideRadioGroup label="Override Credit" defaultValue="off" />
          </div>
          <div className="col-12 col-md-6">
            <OverrideRadioGroup label="Override Amount" defaultValue="off" />
          </div>
          <div className="col-12 col-md-7">
            <label className="override-input">
              <span>Amount*</span>
              <input defaultValue="450.0" />
            </label>
          </div>
          <div className="col-12 col-md-3 align-self-end">
            <label className="override-input">
              <span className="visually-hidden">Currency</span>
              <select defaultValue="LKR">
                <option value="LKR">LKR</option>
                <option value="USD">USD</option>
              </select>
            </label>
          </div>
        </div>
      </div>
      <div className="manage-paper-drawer__footer">
        <SaveButton label="Save changes" onClick={onClose} />
        <CancelButton label="Close" onClick={onClose} />
      </div>
    </aside>
  </>
);

export default function ManagePaperList({ items, onView }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <div className="manage-paper-refresh-row">
        <button
          className="paper-refresh"
          type="button"
          title="Refresh"
          aria-label="Refresh papers"
        >
          <HiArrowPath />
        </button>
      </div>

      <div className="paper-row-list manage-paper-list">
        {items.map((item) => (
          <article
            className="paper-row manage-paper-row row g-2 align-items-center"
            key={item.id}
          >
            <h3 className="col-12 col-md-5">{item.name}</h3>
            <span className="paper-row__code col-12 col-md-2">
              - {item.code}
            </span>
            <div className="col-12 col-md-3">
              <div className="manage-paper-statuses">
                <span className={getStatusClass(item.status)}>
                  {item.status}
                </span>
                {item.secondaryStatus && (
                  <span className="paper-status paper-status--added">
                    {item.secondaryStatus}
                  </span>
                )}
              </div>
            </div>
            <div className="paper-row__actions col-12 col-md-2">
              <MainButton
                action="view"
                icon="view"
                label=""
                title="View paper"
                aria-label="View paper"
                onClick={() => onView?.(item)}
              />
              {item.canAdd && (
                <MainButton
                  action="add"
                  icon="add"
                  label=""
                  title="Add to available exams"
                  aria-label="Add to available exams"
                  onClick={() => setConfirmOpen(true)}
                />
              )}
              {item.canManage && (
                <>
                  <MainButton
                    action="delete"
                    icon="cancel"
                    label=""
                    title="Remove paper"
                    aria-label="Remove paper"
                  />
                  <MainButton
                    action="override"
                    icon="override"
                    label=""
                    title="Override details"
                    aria-label="Override details"
                    onClick={() => setDrawerOpen(true)}
                  />
                </>
              )}
            </div>
          </article>
        ))}
      </div>

      <AddPaperConfirm
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
      />
      <OverrideDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
